import { Modal } from 'common/modal';
import { useContext, useEffect, useRef, useState } from 'react';
import { InterviewListItem } from 'common/interview-list-item';
import { CameraRecord } from 'common/camera-upload/components/camera-record';
import { ModalPosition } from 'common/modal/modal';
import { AlbumHeaderTitle } from 'common/album-header-title';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronIcon,
  CrossIcon,
  DeleteIcon,
  LightBulbIcon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { appActions, AppContext } from 'context';
import { NotificationObject } from 'models/notificationObject';
import {
  AlbumContentType,
  Breakpoints,
  NotificationType,
  RecordingState,
} from 'common/enums';
import { adaptContentType, classnames } from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import ReactPlayer from 'react-player';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { ModalWarning } from 'common/modal-warning';
import { AudioRecorder } from 'common/audio-recorder';
import { HorizontalTabs, TabItem } from 'common/horizontal-tabs';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { DocumentType } from 'networking/types/documents';
import { AudioPlayer } from 'pages/album-view-mode/components/render-content/components/audio-player';
import { publishMemoryAdded } from 'common/returning-onboarding/onboarding-status';
import { RichTextEditor } from 'common/rich-text-editor';
import { HtmlRenderer } from 'common/html-renderer';
import { RecordMode } from 'common/respond-menu';
import { OutOfStorageModal } from 'common/out-of-storage-modal';
import { isOutOfStorage } from 'helpers/storage';
import styles from './record-now.module.scss';

type RecordingMode = 'video' | 'audio';

const RECORDING_TABS: TabItem<RecordingMode>[] = [
  { id: 'video', label: 'Video' },
  { id: 'audio', label: 'Audio' },
];

// What a new custom topic is called until the user types their own.
const CUSTOM_TOPIC_NAME = 'Custom Topic';

type Recording = ClipSerialized | DocumentType | JournalDocumentType;

// A topic answered in this session. Its recording is now a memory of the
// album (`contentId`), and the topic's prompt (`topicId`) has been removed.
type RecordedTopic = {
  contentId: number;
  topicId: number;
  name: string;
  recording: Recording;
};

// A topic to answer, an answer given in this window, or one of the album's
// earlier answers.
type Selection = { kind: 'topic' | 'recorded' | 'answered'; id: number };

// The kinds of memory a topic can be answered with.
const ANSWER_TYPES = [
  AlbumContentType.video,
  AlbumContentType.audio,
  AlbumContentType.write,
];

interface RecordNowProps {
  vaultId: number;
  albumId: number;
  // Shown as where the answers are saved (see albumDisplayName).
  albumName?: string;
  // The album's topics: its prompts that haven't been recorded yet.
  topics: AlbumContentSerialized[];
  // Reloads the album after a topic is added, renamed, answered or restored.
  onTopicsChanged: () => void;
  onClose: () => void;
  // How topics are answered here: a recording (video or audio, with tabs to
  // switch), or a journal entry written in the editor.
  mode?: RecordMode;
  // The topic to start on (the prompt that was clicked).
  initialTopicId?: number;
  // For the storage check; the vault being viewed when not given.
  vault?: Pick<VaultType, 'storageLimit' | 'totalStorage'>;
  // The album's memories, listed under "Answered" with this window's own.
  answered?: AlbumContentSerialized[];
}

// A video answer is a clip and a journal answer a journal document, each
// carrying its memory's id; an audio answer comes back as a document whose id
// is already its content's.
const contentIdOf = (recording: Recording) =>
  'contentId' in recording ? recording.contentId : recording.id;

export const RecordNow = ({
  vaultId,
  albumId,
  albumName,
  topics,
  onTopicsChanged,
  onClose,
  mode = 'video',
  initialTopicId,
  vault: vaultProp,
  answered = [],
}: RecordNowProps) => {
  const isJournal = mode === 'journal';
  // Every answer given in journal mode is a journal document. (A clip may
  // carry a `body` too, so the mode is what tells them apart.)
  const isJournalAnswer = (
    recording: Recording,
  ): recording is JournalDocumentType => isJournal && 'body' in recording;
  const { state: selfRecordingState, dispatch: dispatchSelfRecording } =
    useContext(SelfRecordingContext);
  const { state: appState, dispatch: dispatchApp } = useContext(AppContext);
  const vault = vaultProp ?? appState.currentVault;
  // A recording was refused for storage: the dialog says so, not an error.
  const [showOutOfStorage, setShowOutOfStorage] = useState(false);
  const refusedForStorage = useRef(false);
  const handleOutOfStorage = () => {
    refusedForStorage.current = true;
    setShowOutOfStorage(true);
  };
  const [showDeleteRecordingModal, setShowDeleteRecordingModal] =
    useState(false);
  const [selection, setSelection] = useState<Selection | null>(() => {
    const first =
      topics.find((topic) => topic.id === initialTopicId) ?? topics[0];
    return first ? { kind: 'topic', id: first.id } : null;
  });
  const [recordedTopics, setRecordedTopics] = useState<RecordedTopic[]>([]);
  // Names typed here show right away, before the album reloads.
  const [renamedTopics, setRenamedTopics] = useState<Record<number, string>>(
    {},
  );
  const [recordingMode, setRecordingMode] = useState<RecordingMode>(
    mode === 'audio' ? 'audio' : 'video',
  );
  // Bumped to empty the journal editor (Discard).
  const [editorKey, setEditorKey] = useState(0);
  const [pendingRecordingMode, setPendingRecordingMode] =
    useState<RecordingMode | null>(null);
  const [showCloseWhileRecordingWarning, setShowCloseWhileRecordingWarning] =
    useState(false);

  // Phones: one column, as the mobile app's recorder. Answering a prompt
  // shows just that prompt; Record now folds the topics into a card above
  // the recorder, which closes again once one is picked.
  const isPhone = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const isResponding = initialTopicId != null;
  const [topicsOpen, setTopicsOpen] = useState(false);
  useEffect(() => setTopicsOpen(false), [selection]);

  // A topic answered here stays hidden until the album reloads without it.
  const visibleTopics = topics.filter(
    (topic) => !recordedTopics.some((item) => item.topicId === topic.id),
  );
  const selectedTopic =
    selection?.kind === 'topic'
      ? visibleTopics.find((topic) => topic.id === selection.id)
      : undefined;
  const selectedRecorded =
    selection?.kind === 'recorded'
      ? recordedTopics.find((item) => item.contentId === selection.id)
      : undefined;
  // The album's journal, audio and video memories, less any answered here
  // (they show once, as this window's).
  const earlierAnswers = answered.filter(
    (content) =>
      ANSWER_TYPES.includes(adaptContentType(content)) &&
      !recordedTopics.some((item) => item.contentId === content.id),
  );
  const selectedAnswered =
    selection?.kind === 'answered'
      ? earlierAnswers.find((content) => content.id === selection.id)
      : undefined;
  const topicName = (topic: AlbumContentSerialized) =>
    renamedTopics[topic.id] ?? topic.name;

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatchApp({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        message,
        type,
      }),
    });

  const isRecordingInCurrentMode =
    (recordingMode === 'video' &&
      selfRecordingState.recordingState === RecordingState.RECORDING) ||
    (recordingMode === 'audio' && selfRecordingState.isRecording);

  const handleRecordingModeChange = (newMode: RecordingMode) => {
    if (newMode !== recordingMode && isRecordingInCurrentMode) {
      setPendingRecordingMode(newMode);
      return;
    }
    setRecordingMode(newMode);
  };

  const confirmSwitchTab = () => {
    if (!pendingRecordingMode) return;
    dispatchSelfRecording({ type: selfRecordingActions.cleanState });
    setRecordingMode(pendingRecordingMode);
    setPendingRecordingMode(null);
  };

  const handleRequestClose = () => {
    if (isRecordingInCurrentMode) {
      setShowCloseWhileRecordingWarning(true);
      return;
    }
    onClose();
  };

  const confirmCloseWhileRecording = () => {
    dispatchSelfRecording({ type: selfRecordingActions.cleanState });
    setShowCloseWhileRecordingWarning(false);
    onClose();
  };

  // A recording answers the selected topic: it's saved in the album as a
  // memory named after the topic, and the topic's prompt is removed.
  const onGetContent = async (recording?: Recording) => {
    const topic = selectedTopic;
    if (!recording && refusedForStorage.current) {
      // The out-of-storage dialog is already showing.
      refusedForStorage.current = false;
      return;
    }
    if (!topic || !recording) {
      notify(
        'The recording could not be saved',
        NotificationType.Error,
        'Please try again.',
      );
      return;
    }
    const contentId = contentIdOf(recording);
    setRecordedTopics((prev) => [
      ...prev,
      { contentId, topicId: topic.id, name: topicName(topic), recording },
    ]);
    setSelection({ kind: 'recorded', id: contentId });
    publishMemoryAdded();
    notify(
      isJournal ? 'Answer saved' : 'Topic recorded',
      NotificationType.Success,
    );
    try {
      await VaultsController.deleteAlbumContent(topic.id);
    } catch (error) {
      // The topic stays in the planner; its recording is saved either way.
    }
    onTopicsChanged();
  };

  // Deleting an answer removes its memory and puts the topic back.
  const onDeleteRecording = async () => {
    const item = selectedRecorded;
    setShowDeleteRecordingModal(false);
    if (!item) return;
    try {
      await VaultsController.deleteAlbumContent(item.contentId);
    } catch (error) {
      notify(
        'The recording could not be deleted',
        NotificationType.Error,
        'Please try again.',
      );
      return;
    }
    setRecordedTopics((prev) =>
      prev.filter((recorded) => recorded.contentId !== item.contentId),
    );
    setSelection(null);
    notify(
      isJournal ? 'Answer deleted' : 'Recording deleted',
      NotificationType.Success,
    );
    try {
      const topic = await VaultsController.createClip(vaultId, {
        clip: { name: item.name, albumId },
      });
      setSelection({ kind: 'topic', id: topic.contentId });
    } catch (error) {
      // The topic can be added again from the planner.
    }
    onTopicsChanged();
  };

  // A custom topic is a new prompt in this album, named by the user.
  const handleAddCustomTopic = async () => {
    try {
      const topic = await VaultsController.createClip(vaultId, {
        clip: { name: CUSTOM_TOPIC_NAME, albumId },
      });
      setSelection({ kind: 'topic', id: topic.contentId });
      onTopicsChanged();
      notify('Custom topic added', NotificationType.Success);
    } catch (error) {
      notify(
        'The topic could not be added',
        NotificationType.Error,
        'Please try again.',
      );
    }
  };

  useEffect(() => {
    dispatchSelfRecording({ type: selfRecordingActions.setVaultId, vaultId });
    dispatchSelfRecording({ type: selfRecordingActions.setAlbumId, albumId });
  }, [dispatchSelfRecording, vaultId, albumId]);

  const topicsList = (
    <div className={styles.albumsList}>
      <span className="text__body__regular__small__gray500">Topics</span>
      <div className={styles.albumsListItems}>
        <InterviewListItem
          title={CUSTOM_TOPIC_NAME}
          type="customTopic"
          onClick={handleAddCustomTopic}
        />
        {visibleTopics.length > 0 ? (
          visibleTopics.map((topic) => (
            <InterviewListItem
              key={topic.id}
              title={topicName(topic)}
              type="unanswered"
              isSelected={
                selection?.kind === 'topic' && selection.id === topic.id
              }
              onClick={() => setSelection({ kind: 'topic', id: topic.id })}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <LightBulbIcon />
            </div>
            <span className="text__body__regular__small__gray700">
              You haven&apos;t added any topics.
            </span>
            <span className="text__body__regular__small__gray400">
              Add a custom topic above, or pick topics in the{' '}
              <Button
                buttonSize={ButtonSize.Small}
                buttonStyle={ButtonStyle.PrimaryGhostLink}
                onClick={onClose}
              >
                Planner
              </Button>
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const recordedList = (
    <div className={styles.albumsList}>
      <span className="text__body__regular__small__gray500">Answered</span>
      <div className={styles.albumsListItems}>
        {recordedTopics.length > 0 || earlierAnswers.length > 0 ? (
          <>
            {recordedTopics.map((item) => (
              <InterviewListItem
                key={item.contentId}
                title={item.name}
                type="unanswered"
                recordedInThisSession
                isSelected={
                  selection?.kind === 'recorded' &&
                  selection.id === item.contentId
                }
                onClick={() =>
                  setSelection({ kind: 'recorded', id: item.contentId })
                }
              />
            ))}
            {earlierAnswers.map((content) => (
              <InterviewListItem
                key={content.id}
                title={content.name || 'Untitled'}
                type="unanswered"
                isSelected={
                  selection?.kind === 'answered' && selection.id === content.id
                }
                onClick={() =>
                  setSelection({ kind: 'answered', id: content.id })
                }
              />
            ))}
          </>
        ) : (
          <div className={styles.emptyStateAnswered}>
            <span className="text__body__regular__small__gray700">
              Topics you answer will show up here
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const answeredCount = recordedTopics.length + earlierAnswers.length;
  const pickedName =
    (selectedTopic && topicName(selectedTopic)) ||
    selectedRecorded?.name ||
    (selectedAnswered && (selectedAnswered.name || 'Untitled'));
  const topicPicker = (
    <div className={styles.topicPicker}>
      <button
        type="button"
        className={styles.topicPickerHeader}
        aria-expanded={topicsOpen}
        onClick={() => setTopicsOpen((open) => !open)}
      >
        <span className={styles.topicPickerIcon}>
          {selectedRecorded || selectedAnswered ? (
            <CheckIcon />
          ) : (
            <LightBulbIcon />
          )}
        </span>
        <span className={styles.topicPickerText}>
          <span className={styles.topicPickerLabel}>
            Topics · {visibleTopics.length} to answer
            {answeredCount > 0 && ` · ${answeredCount} answered`}
          </span>
          <span
            className={classnames(styles.topicPickerTitle, {
              [styles.topicPickerPlaceholder]: !pickedName,
            })}
          >
            {pickedName || 'Choose a topic'}
          </span>
        </span>
        <ChevronIcon
          className={classnames(styles.topicPickerChevron, {
            [styles.topicPickerChevronOpen]: topicsOpen,
          })}
        />
      </button>
      {topicsOpen && (
        <div className={styles.topicPickerList}>
          {topicsList}
          <div className={styles.divider} />
          {recordedList}
        </div>
      )}
    </div>
  );

  // An earlier answer, read-only: its text, recording or video.
  const renderAnswered = (content: AlbumContentSerialized) => {
    const type = adaptContentType(content);
    if (type === AlbumContentType.write) {
      return (
        <div className={styles.journalAnswerContainer}>
          <HtmlRenderer
            className={styles.journalAnswer}
            html={content.content?.body ?? ''}
          />
        </div>
      );
    }
    const url = content.mediaUrl || content.content?.videoUrl;
    if (!url) {
      return (
        <div className={styles.noTopic}>
          <span className="text__body__regular__small__gray500">
            This recording is still being processed.
          </span>
        </div>
      );
    }
    if (type === AlbumContentType.audio) {
      return (
        <div className={styles.audioPlayerContainer}>
          <AudioPlayer
            audioUrl={url}
            isPlaying={false}
            isFullScreen={false}
            showControls
          />
        </div>
      );
    }
    return (
      <div className={styles.videoPlayerContainer}>
        <ReactPlayer
          url={url}
          controls
          config={{
            file: { attributes: { controlsList: 'nodownload noplaybackrate' } },
          }}
          style={{ borderRadius: '16px' }}
          width="100%"
          height="auto"
          className={styles.videoPlayer}
        />
      </div>
    );
  };

  const renderRecording = (recording: Recording) => {
    const deleteButton = (
      <div className={styles.videoPlayerControls}>
        <Button
          buttonSize={ButtonSize.Small}
          buttonStyle={ButtonStyle.DestructiveSecondary}
          onClick={() => setShowDeleteRecordingModal(true)}
        >
          <div className={styles.deleteRecording}>
            <DeleteIcon />
            {isJournal ? 'Delete answer' : 'Delete recording'}
          </div>
        </Button>
      </div>
    );

    // A journal answer: its text, as the album shows it.
    if (isJournalAnswer(recording)) {
      return (
        <div className={styles.journalAnswerContainer}>
          <HtmlRenderer
            className={styles.journalAnswer}
            html={recording.body}
          />
          {deleteButton}
        </div>
      );
    }

    // An audio answer comes back as a document (it has a fileUrl); a video
    // answer is a clip. Test for the document: a clip's videoUrl is optional,
    // so testing for it wouldn't narrow the other branch to a document.
    if ('fileUrl' in recording) {
      return (
        <div className={styles.audioPlayerContainer}>
          <AudioPlayer
            audioUrl={recording.fileUrl}
            isPlaying
            isFullScreen={false}
            showControls
          />
          {deleteButton}
        </div>
      );
    }

    return (
      <div className={styles.videoPlayerContainer}>
        <ReactPlayer
          url={recording.videoUrl}
          controls
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload noplaybackrate',
              },
            },
          }}
          style={{ borderRadius: '16px' }}
          width="100%"
          height="auto"
          className={styles.videoPlayer}
        />
        {deleteButton}
      </div>
    );
  };

  // A full vault can't take a recording, as in the "Add entry" dialog (a
  // journal answer is text and still allowed).
  if (!isJournal && vault && isOutOfStorage(vault)) {
    return <OutOfStorageModal vault={vault} onClose={onClose} />;
  }

  return (
    <>
      {showDeleteRecordingModal && (
        <ModalWarning
          title={isJournal ? 'Delete Answer' : 'Delete Recording'}
          content={
            isJournal
              ? 'Are you sure you want to delete this answer? Its topic goes back to your list.'
              : 'Are you sure you want to delete this recording? Its topic goes back to your list.'
          }
          closeFn={() => setShowDeleteRecordingModal(false)}
          successFn={onDeleteRecording}
          successButtonText={isJournal ? 'Delete Answer' : 'Delete Recording'}
          successStyleButton={ButtonStyle.RedFilled}
        />
      )}
      {pendingRecordingMode && (
        <ModalWarning
          title={
            pendingRecordingMode === 'audio'
              ? 'Switch to Audio?'
              : 'Switch to Video?'
          }
          content={
            <div className="text__body__regular__medium__gray800">
              {pendingRecordingMode === 'audio'
                ? 'Are you sure you want to switch to Audio? Your video recording will be cancelled.'
                : 'Are you sure you want to switch to Video? Your audio recording will be cancelled.'}
            </div>
          }
          successButtonText="Switch & cancel recording"
          successStyleButton={ButtonStyle.RedFilled}
          closeFn={() => setPendingRecordingMode(null)}
          successFn={confirmSwitchTab}
        />
      )}
      {showCloseWhileRecordingWarning && (
        <ModalWarning
          title="Leaving page"
          content="Are you sure you want to leave? If you leave your recording will be cancelled."
          successButtonText="Leave & cancel upload"
          successStyleButton={ButtonStyle.RedFilled}
          closeFn={() => setShowCloseWhileRecordingWarning(false)}
          successFn={confirmCloseWhileRecording}
        />
      )}
      <Modal
        disableManualClosing
        contentClassName={styles.modalContainer}
        position={ModalPosition.Center}
      >
        <div className={styles.container}>
          {!isPhone && (
            <div className={styles.leftColumn}>
              {topicsList}
              <div className={styles.divider} />
              {recordedList}
            </div>
          )}
          <div className={styles.rightColumn}>
            {isPhone && (
              <div className={styles.phoneBar}>
                <button
                  type="button"
                  className={styles.phoneBack}
                  onClick={handleRequestClose}
                >
                  <ArrowLeftIcon />
                  Back
                </button>
              </div>
            )}
            {isPhone && !isResponding && topicPicker}
            <div className={styles.rightHeader}>
              {selectedTopic && (
                <AlbumHeaderTitle
                  key={selectedTopic.id}
                  title={topicName(selectedTopic)}
                  albumId={albumId}
                  save={(name) =>
                    VaultsController.updateContentMetadata(selectedTopic.id, {
                      name,
                    })
                  }
                  onTitleChange={(name) => {
                    setRenamedTopics((prev) => ({
                      ...prev,
                      [selectedTopic.id]: name,
                    }));
                    onTopicsChanged();
                  }}
                  savedMessage="Topic updated"
                  placeholder="Type your topic"
                  emptyTitle={CUSTOM_TOPIC_NAME}
                />
              )}
              {selectedRecorded && (
                <AlbumHeaderTitle
                  title={selectedRecorded.name}
                  albumId={albumId}
                  editable={false}
                />
              )}
              {selectedAnswered && (
                <AlbumHeaderTitle
                  title={selectedAnswered.name || 'Untitled'}
                  albumId={albumId}
                  editable={false}
                />
              )}
              {!isPhone && (
                <button
                  type="button"
                  onClick={handleRequestClose}
                  className={styles.crossIcon}
                  aria-label="Close"
                >
                  <CrossIcon />
                </button>
              )}
            </div>
            {albumName && (
              <p className={styles.destination}>
                Saves to <strong>{albumName}</strong>
              </p>
            )}
            {selectedRecorded && renderRecording(selectedRecorded.recording)}
            {selectedAnswered && renderAnswered(selectedAnswered)}
            {selectedTopic && isJournal && (
              <div className={styles.journalEditorContainer}>
                <RichTextEditor
                  key={`${selectedTopic.id}-${editorKey}`}
                  title={topicName(selectedTopic)}
                  placeholder="Write your answer…"
                  saveLabel="Save answer"
                  quiet
                  onClose={() => setEditorKey((key) => key + 1)}
                  onCreated={onGetContent}
                />
              </div>
            )}
            {selectedTopic && !isJournal && (
              <>
                <div className={styles.recordingModeTabsContainer}>
                  <HorizontalTabs
                    tabs={RECORDING_TABS}
                    activeTab={recordingMode}
                    onChange={handleRecordingModeChange}
                  />
                </div>
                <div className={styles.cameraRecordingContainer}>
                  {recordingMode === 'video' ? (
                    <CameraRecord
                      onStopRecording={onGetContent}
                      onOutOfStorage={handleOutOfStorage}
                      showToggle={false}
                      updateUploads={false}
                      title={topicName(selectedTopic)}
                    />
                  ) : (
                    <AudioRecorder
                      onStopRecording={onGetContent}
                      onOutOfStorage={handleOutOfStorage}
                      showToggle={false}
                      title={topicName(selectedTopic)}
                    />
                  )}
                </div>
              </>
            )}
            {!selectedTopic && !selectedRecorded && !selectedAnswered && (
              <div className={styles.noTopic}>
                <span className="text__body__regular__small__gray500">
                  {`Pick a topic ${isPhone ? 'above' : 'on the left'}, or add a custom one, to start ${isJournal ? 'writing' : 'recording'}.`}
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {showOutOfStorage && vault && (
        <OutOfStorageModal
          vault={vault}
          onClose={() => setShowOutOfStorage(false)}
        />
      )}
    </>
  );
};
